import { Axios } from '@utils/api/axios-instance.ts';
import { API_ROOT } from '@utils/constants/app-constants.ts';
import { LiveUtterance } from '@utils/types/editors/live-transcript.ts';
import { BaseResponse } from '@utils/types/shared/base.ts';

export type RecordingCandidate = {
  session_id: string;
  filename: string;
  status: string;
  date_session_start: string;
};
type RecordingListItem = Omit<RecordingCandidate, 'session_id' | 'filename'> & {
  id: string;
  filename: string | null;
};

export type VodVersion = {
  version_id: string;
  applied_segments: number;
  protected_segments: number;
  unmatched_segments: number;
  unmatched_recording_segments: number;
};
export type VodRecording = {
  recording_session_id: string;
  filename: string | null;
  offset_ms: number;
  pending: boolean;
  versions: VodVersion[];
};
export type VodGeneratedVersion = VodVersion & {
  document: { document: LiveUtterance[] };
};
const root = (session: string) =>
  `${API_ROOT}/speech-to-text/session/${encodeURIComponent(session)}/live/vod`;
export const getLiveRecordings = async (session: string) =>
  (await Axios.get<VodRecording[]>(root(session))).data;
export const associateLiveRecording = async (
  session: string,
  recording: string,
  offset: number
) =>
  (
    await Axios.post<VodRecording[]>(root(session), {
      recording_session_id: recording,
      offset_ms: offset
    })
  ).data;
export const getLiveGeneratedVersion = async (
  session: string,
  version: string
) =>
  (
    await Axios.get<VodGeneratedVersion>(
      `${root(session)}/version/${encodeURIComponent(version)}`
    )
  ).data;
export const searchRecordings = async (
  q: string,
  signal: AbortSignal
): Promise<RecordingCandidate[]> => {
  const data = (
    await Axios.get<BaseResponse<RecordingListItem[]>>(
      `${API_ROOT}/speech-to-text/session`,
      {
        params: {
          q: q.trim() ? `filename:ilike:${q.trim()}` : undefined,
          offset: 0,
          limit: 50,
          sort: 'date_session_start',
          direction: 'desc'
        },
        signal
      }
    )
  ).data;
  return data.result.map(item => ({
    session_id: item.id,
    filename: item.filename ?? '',
    status: item.status,
    date_session_start: item.date_session_start
  }));
};
